import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Users, TrendingUp, DollarSign, Award, Zap, ShieldCheck, ArrowRight, Star, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function MLM() {
  const { t } = useTranslation();
  const [levels, setLevels] = React.useState([]);

  React.useEffect(() => {
    import("../services/api").then(({ default: api }) => {
      api.get("/public/mlm").then(res => {
        if (res.success && res.data.levels) {
          setLevels(res.data.levels.map(l => ({
            level: `${t('mlm.level')} ${l.level_number}`,
            commission: `${l.commission_percent}%`,
            requirement: l.min_team_required > 0 ? `${l.min_team_required} ${t('mlm.team_members')}` : t('mlm.direct'),
            icon: <Users className={l.level_number === 1 ? "text-indigo-400" : l.level_number === 2 ? "text-cyan-400" : "text-emerald-400"} />
          })));
        }
      });
    });
  }, [t]);

  return (
    <div className="min-h-screen bg-slate-950 text-white font-inter">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.15)_0%,transparent_50%)] pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 mb-8">
            <Zap size={14} className="text-indigo-400 animate-pulse" />
            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest leading-none">{t('mlm.affiliate_pro')}</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-8 tracking-tighter leading-tight font-outfit uppercase">
            {t('mlm.title').split(' ').slice(0, -2).join(' ')} <br />
            <span className="bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              {t('mlm.title').split(' ').slice(-2).join(' ')}
            </span>
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto font-medium leading-relaxed">
            {t('mlm.subtitle')}
          </p>
          <div className="mt-12 flex justify-center">
            <Link to="/register" className="btn-premium !px-12 !py-5 shadow-indigo-500/20 flex items-center gap-3">
              <span className="font-black text-sm uppercase tracking-widest">{t('mlm.start_referring')}</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Rewards Grid */}
      <section className="py-24 relative bg-white/[0.02] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-10 border-white/10 hover:border-indigo-500/30 transition-all group">
              <div className="w-14 h-14 bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-8 border border-indigo-500/20 group-hover:scale-110 transition-transform">
                <DollarSign className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-black mb-4 font-outfit uppercase tracking-tight">{t('mlm.direct_commissions')}</h3>
              <p className="text-slate-500 font-semibold leading-relaxed">
                {t('mlm.direct_comm_desc')}
              </p>
            </div>

            <div className="glass-card p-10 border-white/10 hover:border-cyan-500/30 transition-all group">
              <div className="w-14 h-14 bg-cyan-500/10 rounded-2xl flex items-center justify-center mb-8 border border-cyan-500/20 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-black mb-4 font-outfit uppercase tracking-tight">{t('mlm.volume_bonus')}</h3>
              <p className="text-slate-500 font-semibold leading-relaxed">
                {t('mlm.volume_bonus_desc')}
              </p>
            </div>

            <div className="glass-card p-10 border-white/10 hover:border-emerald-500/30 transition-all group">
              <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-8 border border-emerald-500/20 group-hover:scale-110 transition-transform">
                <Award className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-black mb-4 font-outfit uppercase tracking-tight">{t('mlm.leader_status')}</h3>
              <p className="text-slate-500 font-semibold leading-relaxed">
                {t('mlm.leader_status_desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Level Structure */}
      <section className="py-24 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
                <h2 className="text-4xl font-black font-outfit uppercase tracking-tight mb-4">{t('mlm.unilevel_structure')}</h2>
                <p className="text-slate-400 font-medium">{t('mlm.fair_distribution')}</p>
            </div>
          <div className="space-y-4">
            {levels.map((item, idx) => (
              <div key={idx} className="glass-card p-6 border-white/5 flex items-center justify-between hover:bg-white/5 transition-colors cursor-default group">
                <div className="flex items-center gap-6">
                  <div className="p-3 bg-white/5 rounded-xl border border-white/5 group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white tracking-tight">{item.level}</h4>
                    <p className="text-xs font-black text-slate-500 uppercase tracking-widest">{item.requirement}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-3xl font-black bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent font-outfit">{item.commission}</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 p-8 glass-card border-indigo-500/10 flex flex-col md:flex-row items-center justify-between gap-8 shadow-indigo-500/5 transition-all">
                <div className="flex items-center gap-6">
                    <div className="p-4 bg-indigo-500/10 rounded-full text-indigo-400">
                        <Globe size={40} />
                    </div>
                    <div>
                        <h4 className="text-xl font-black text-white font-outfit uppercase">{t('mlm.ready_to_build')}</h4>
                        <p className="text-slate-400 font-medium tracking-tight">{t('mlm.use_referral')}</p>
                    </div>
                </div>
                <Link to="/register" className="btn-premium !py-4 px-8 shadow-indigo-500/20 font-black text-xs uppercase tracking-widest">
                    {t('auth.open_pro_account')}
                </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
